(() => {
  if (customElements.get('kr-size-guide')) return;
  const openGuides = new Set();
  let scrollState;
  const lockScroll = (guide) => {
    if (openGuides.has(guide)) return;
    if (openGuides.size === 0) {
      const html = document.documentElement;
      const body = document.body;
      const scrollbar = Math.max(0, window.innerWidth - html.clientWidth);
      const containers = [...new Set([html, body, ...document.querySelectorAll('.page-wrapper')])];
      scrollState = {
        containers: containers.map((element) => ({
          element,
          overflow: ['overflow', 'overflow-x', 'overflow-y'].map((property) => ({
            property,
            value: element.style.getPropertyValue(property),
            priority: element.style.getPropertyPriority(property),
          })),
        })),
        paddingRight: body.style.getPropertyValue('padding-right'),
        paddingPriority: body.style.getPropertyPriority('padding-right'),
      };
      containers.forEach((element) => element.style.setProperty('overflow', 'hidden', 'important'));
      if (scrollbar) body.style.paddingRight = `${parseFloat(getComputedStyle(body).paddingRight) + scrollbar}px`;
    }
    openGuides.add(guide);
  };
  const unlockScroll = (guide) => {
    openGuides.delete(guide);
    if (openGuides.size || !scrollState) return;
    scrollState.containers.forEach(({ element, overflow }) => {
      overflow.forEach(({ property }) => element.style.removeProperty(property));
      overflow.forEach(({ property, value, priority }) => {
        if (value) element.style.setProperty(property, value, priority);
      });
    });
    if (scrollState.paddingRight) document.body.style.setProperty('padding-right', scrollState.paddingRight, scrollState.paddingPriority);
    else document.body.style.removeProperty('padding-right');
    scrollState = undefined;
  };
  class KrSizeGuide extends HTMLElement {
    connectedCallback() {
      if (this.controller) return;
      this.dialog = this.querySelector('[data-kr-size-guide-dialog]');
      if (!this.dialog) return;
      this.controller = new AbortController();
      const options = { signal: this.controller.signal };
      this.syncDetailsTarget();
      document.addEventListener('DOMContentLoaded', () => this.syncDetailsTarget(), options);
      try { this.variantData = JSON.parse(this.querySelector('[data-kr-size-guide-variants]')?.textContent || '{}'); }
      catch { this.variantData = {}; }
      document.addEventListener('click', (event) => {
        const trigger = event.target.closest('[data-kr-size-guide-open]');
        if (!trigger || trigger.dataset.krSizeGuideOpen !== this.dialog.id) return;
        event.preventDefault();
        this.open(trigger);
      }, options);
      this.dialog.addEventListener('click', (event) => {
        if (event.target.closest('[data-kr-size-guide-close]')) this.close();
        if (event.target.closest('[data-kr-size-guide-details]')) {
          const target = this.syncDetailsTarget();
          if (target && !event.ctrlKey && !event.metaKey && !event.shiftKey && !event.altKey && event.button === 0) {
            event.preventDefault();
            this.close(false);
            if (target.id === `KrProductDetails-${this.dataset.sectionId}`) {
              const description = target.querySelector('details');
              if (description) description.open = true;
            }
            if (!target.hasAttribute('tabindex')) target.setAttribute('tabindex', '-1');
            target.focus({ preventScroll: true });
            target.scrollIntoView({ behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth', block: 'start' });
          } else {
            this.close();
          }
        }
        if (event.target === this.dialog) {
          const rect = this.dialog.getBoundingClientRect();
          if (event.clientX < rect.left || event.clientX > rect.right || event.clientY < rect.top || event.clientY > rect.bottom) this.close();
        }
      }, options);
      this.dialog.addEventListener('close', () => this.afterClose(), options);
      this.dialog.addEventListener('cancel', (event) => { event.preventDefault(); this.close(); }, options);
      document.addEventListener('change', (event) => {
        if (this.dialog.open && event.target.matches('[data-kr-variant], input[name="id"]') && this.productRoot()?.contains(event.target)) this.syncVariant();
      }, options);
      document.addEventListener('shopify:section:unload', (event) => {
        if (event.target.contains(this)) this.close();
      }, options);
    }
    productRoot() {
      return this.closest('.shopify-section') || document.getElementById(`shopify-section-${this.dataset.sectionId}`) || this.parentElement;
    }
    syncDetailsTarget() {
      const target = document.getElementById('kr-fit') || document.getElementById(`KrProductDetails-${this.dataset.sectionId}`);
      if (!target) return null;
      this.querySelector('[data-kr-size-guide-details]')?.setAttribute('href', `#${target.id}`);
      this.productRoot()?.querySelectorAll('[data-kr-size-guide-open]').forEach((trigger) => {
        if (trigger.dataset.krSizeGuideOpen === this.dialog.id) trigger.setAttribute('href', `#${target.id}`);
      });
      return target;
    }
    syncVariant() {
      const selector = this.productRoot()?.querySelector('[data-kr-variant], form[action*="/cart/add"] input[name="id"]');
      const selectedId = String(selector?.value || this.variantData.selectedId || '');
      const variant = this.variantData.variants?.find((entry) => String(entry.id) === selectedId);
      if (!variant) return;
      const image = this.querySelector('.kr-size-guide__image');
      if (image && variant.image) {
        image.removeAttribute('srcset');
        image.src = variant.image;
        image.alt = variant.alt || '';
      }
      const label = this.querySelector('[data-kr-size-guide-variant]');
      if (label) label.textContent = variant.title === 'Default Title' ? '' : variant.title;
      const size = String(variant.size || '').trim().toLowerCase();
      this.querySelectorAll('[data-kr-size-row]').forEach((row) => {
        const current = Boolean(size && row.dataset.krSizeRow.split(',').includes(size));
        row.dataset.currentSize = String(current);
        if (current) row.setAttribute('aria-current', 'true');
        else row.removeAttribute('aria-current');
      });
    }
    open(trigger) {
      if (this.dialog.open) return;
      for (const guide of openGuides) if (guide !== this) guide.close();
      this.trigger = trigger;
      this.restoreFocus = true;
      this.syncDetailsTarget();
      this.syncVariant();
      this.dialog.showModal();
      this.trigger.setAttribute('aria-expanded', 'true');
      lockScroll(this);
      this.dialog.querySelector('[data-kr-size-guide-close]')?.focus({ preventScroll: true });
    }
    close(restoreFocus = true) {
      this.restoreFocus = restoreFocus;
      if (this.dialog?.open) this.dialog.close();
      this.afterClose();
    }
    afterClose() {
      if (this.dialog?.open) return;
      unlockScroll(this);
      const trigger = this.trigger;
      this.trigger = null;
      trigger?.setAttribute('aria-expanded', 'false');
      if (this.restoreFocus !== false && trigger?.isConnected && !document.querySelector('dialog[open]')) trigger.focus({ preventScroll: true });
    }
    disconnectedCallback() {
      this.close();
      this.controller?.abort();
      this.controller = null;
    }
  }
  customElements.define('kr-size-guide', KrSizeGuide);
})();

